import { useState } from 'react';
import { CommonSelectProps, SelectBase } from './SelectBase';
import { getSelectOptionValue } from './selectOptionValue';

type NoddiSelectProps<T = string> = Omit<
  CommonSelectProps<T>,
  'isLoading' | 'loadingText' | 'noOptionsUnlessSearched'
> &
  (
    | {
        /** Controlled mode: current value and onChange handler */
        value: T | undefined;
        onChange: (value: T | undefined) => void;
        defaultValue?: never;
      }
    | {
        /** Uncontrolled mode: default value and optional onChange handler */
        defaultValue?: T;
        onChange?: (value: T | undefined) => void;
        value?: never;
      }
  );

export function NoddiSelect<T = string>(props: NoddiSelectProps<T>) {
  const { options = [], value, defaultValue, onChange, onOpenChange, ...baseSelectProps } = props;

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [searchTerm, setSearchTerm] = useState('');

  // Determine if this is controlled or uncontrolled
  const isControlled = 'value' in props;
  const currentValue = isControlled ? value : internalValue;

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);

    if (!newOpen) {
      setSearchTerm('');
    }
  };

  const updateValue = (newValue: T | undefined) => {
    if (isControlled) {
      onChange?.(newValue);
    } else {
      setInternalValue(newValue);
      onChange?.(newValue);
    }
  };

  const handleValueChange = (newValue: string) => {
    // Extract the actual option value from the combined search string
    const selectedOption = options.find((option) => getSelectOptionValue(option) === newValue);
    if (selectedOption) {
      const finalValue = selectedOption.value === currentValue ? undefined : selectedOption.value;

      updateValue(finalValue);
    } else {
      updateValue(undefined);
    }

    setOpen(false);
  };

  return (
    <SelectBase
      {...baseSelectProps}
      options={options}
      open={open}
      setOpen={setOpen}
      currentValue={currentValue}
      onValueChange={handleValueChange}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      onOpenChange={handleOpenChange}
      isLocalFiltering // Local filtering enabled
    />
  );
}
